import { api, authStore, API_URL, apiFetch, snakeToCamel } from '@/core/api/client'

export interface PBUser {
  id: string
  email: string
  name?: string
  role?: 'admin' | 'instructor' | 'staff' | 'company_admin' | 'student'
  status?: 'active' | 'inactive' | 'pending'
  phone?: string
  company?: string | null
  position?: string
  department?: string
  employeeId?: string
  isActiveAccount?: boolean
  dateJoined?: string
  notificationPreferences?: Record<string, boolean>
}

const usersCollection = api.collection<PBUser>('users')

// Auth endpoints return the user with snake_case keys via raw fetch (no case
// conversion like the collection client). Normalize to camelCase here, but keep
// the notification preference keys untouched — the backend's preference dict
// uses snake_case keys (e.g. "checkin_reminder") that the UI references directly.
function normalizeUser(raw: Record<string, unknown>): PBUser {
  const user = snakeToCamel<PBUser>(raw)
  user.notificationPreferences =
    (raw['notification_preferences'] as Record<string, boolean> | undefined) ?? {}
  return user
}

export async function authWithPassword(email: string, password: string): Promise<PBUser> {
  const res = await fetch(`${API_URL}/auth/login/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'Authentication failed.')
  }

  const data = await res.json()
  authStore.save(data.access, data.refresh)
  return normalizeUser(data.user)
}

export async function refreshAuth(): Promise<PBUser> {
  const token = authStore.token
  if (!token) {throw new Error('No token available')}

  const res = await fetch(`${API_URL}/auth/me/`, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (!res.ok) {
    // Attempt refresh
    const refreshed = await fetch(`${API_URL}/auth/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: authStore.refreshToken }),
    })

    if (!refreshed.ok) {
      authStore.clear()
      throw new Error('Session expired.')
    }

    const data = await refreshed.json()
    authStore.save(data.access, authStore.refreshToken || '')

    // Retry me request
    const retry = await fetch(`${API_URL}/auth/me/`, {
      headers: { Authorization: `Bearer ${data.access}` },
    })
    if (!retry.ok) {
      throw new Error('Failed to refresh session.')
    }
    return normalizeUser(await retry.json())
  }

  return normalizeUser(await res.json())
}

export const NOTIFICATION_PREFERENCE_KEYS = [
  'checkin_reminder',
  'class_starting',
  'close_reminder',
  'sale_notification',
] as const

export type NotificationPreferences = Partial<Record<(typeof NOTIFICATION_PREFERENCE_KEYS)[number], boolean>>

export async function updateNotificationPreferences(
  prefs: NotificationPreferences,
): Promise<Record<string, boolean>> {
  const res = await apiFetch(`${API_URL}/auth/me/notification_preferences/`, {
    method: 'PATCH',
    body: JSON.stringify(prefs),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to update notification preferences.')
  }
  const data = await res.json()
  return data.notification_preferences
}

export async function updateCurrentUser(data: {
  name?: string
  phone?: string
  email?: string
}): Promise<PBUser> {
  const token = authStore.token
  if (!token) {throw new Error('No token available')}

  const res = await fetch(`${API_URL}/auth/me/`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to update profile.')
  }

  return normalizeUser(await res.json())
}

export async function changePassword(data: {
  currentPassword: string
  newPassword: string
}): Promise<{ success: boolean; message: string }> {
  const token = authStore.token
  if (!token) {throw new Error('No token available')}

  const res = await fetch(`${API_URL}/auth/change-password/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      current_password: data.currentPassword,
      new_password: data.newPassword,
    }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to change password.')
  }

  return res.json()
}

export function logout(): void {
  authStore.clear()
}

export async function requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
  const res = await fetch(`${API_URL}/users/request-password-reset/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })

  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data.detail || 'Failed to request password reset.')
  }
  return data
}

export async function confirmPasswordReset(code: string, newPassword: string): Promise<{ success: boolean; message: string }> {
  const res = await fetch(`${API_URL}/users/confirm-password-reset/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, new_password: newPassword }),
  })

  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data.detail || 'Failed to reset password.')
  }
  return data
}

export async function listUsers(page = 1, perPage = 50): Promise<PBUser[]> {
  const result = await usersCollection.getList(page, perPage, { sort: '-date_joined' })
  return result.results
}

export async function getAllUsers(): Promise<PBUser[]> {
  return usersCollection.getFullList({ sort: '-date_joined' })
}

export async function fetchUsers(): Promise<PBUser[]> {
  return getAllUsers()
}

export async function getUser(id: string): Promise<PBUser> {
  return usersCollection.getOne(id)
}

export async function createUser(data: {
  email: string
  password: string
  name?: string
  role?: 'admin' | 'instructor' | 'staff' | 'company_admin' | 'student'
  status?: 'active' | 'inactive' | 'pending'
}): Promise<PBUser> {
  return usersCollection.create({
    ...data,
    role: data.role ?? 'staff',
    status: data.status ?? 'active',
  })
}

export async function updateUser(
  id: string,
  data: Partial<{
    name: string
    role: 'admin' | 'instructor' | 'staff' | 'company_admin' | 'student'
    status: 'active' | 'inactive' | 'pending'
  }>
): Promise<PBUser> {
  return usersCollection.update(id, data)
}

export async function deleteUser(id: string): Promise<boolean> {
  return usersCollection.delete(id)
}

export async function getUsersByRole(role: string): Promise<PBUser[]> {
  const all = await usersCollection.getFullList({ sort: 'name' })
  return all.filter((u) => u.role === role)
}

export async function getActiveUsers(): Promise<PBUser[]> {
  const all = await usersCollection.getFullList({ sort: 'name' })
  return all.filter((u) => u.status === 'active')
}
